/**
 * IndexedDB cache for conversation sidebar metadata.
 *
 * Responsibilities:
 * - Instant sidebar render on app refresh (stale-while-revalidate)
 * - Persist socket-received lastMessage/unreadCount across page reloads
 * - Conflict resolution: newer `lastUpdated` timestamp always wins
 * - 50ms debounced writes prevent rapid socket bursts from thrashing IDB
 *
 * DB: "efone-conv-cache", object store: "conversations"
 */

const DB_NAME = "efone-conv-cache";
const DB_VERSION = 1;
const STORE = "conversations";

export interface CachedConversation {
  // Identity
  id: string;
  // Stable display fields (updated rarely — server wins on conflict)
  name: string;
  number: string | null;
  isGroup: boolean;
  isBroadCast: boolean;
  iseFone: boolean;
  sourceNumber: string;
  image?: string;
  groupMembers?: any[];
  broadCastMembers?: any[];
  groupSettings?: any;
  savedName?: string;
  // User settings
  isMuted: boolean;
  isPin: boolean;
  isArchived: boolean;
  isBlocked: boolean;
  // Volatile fields (change with every message — local wins if newer)
  message: string;
  mediaType: number;
  time: string;
  date: string;
  unreadCount: number;
  lastUpdateToSort: string;
  // ISO timestamp of the last message the local user has read.
  // Used as the single source of truth for unread divider placement.
  lastReadAt?: string;
  // Conflict resolution — ISO timestamp, newer always wins
  lastUpdated: string;
}

// ── DB open ────────────────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Per-id write debounce ──────────────────────────────────────────────────

const DEBOUNCE_MS = 50;

interface PendingWrite {
  convo: CachedConversation;
  timer: ReturnType<typeof setTimeout>;
}

const pendingWrites = new Map<string, PendingWrite>();

// ── Public API ─────────────────────────────────────────────────────────────

const conversationCacheDB = {
  /**
   * Write a single conversation with 50ms debounce.
   * Rapid successive calls for the same id are collapsed into one write
   * using the latest data. The actual IDB write is skipped if a newer
   * entry already exists (prevents stale async writes from winning).
   */
  put(convo: CachedConversation): void {
    const pending = pendingWrites.get(convo.id);
    if (pending) clearTimeout(pending.timer);

    const timer = setTimeout(async () => {
      pendingWrites.delete(convo.id);
      try {
        const db = await openDB();
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);

        const existing: CachedConversation | undefined = await new Promise(
          (res) => {
            const r = store.get(convo.id);
            r.onsuccess = () => res(r.result);
            r.onerror = () => res(undefined);
          },
        );

        if (!existing || convo.lastUpdated >= existing.lastUpdated) {
          store.put(convo);
        }

        tx.oncomplete = () => db.close();
        tx.onerror = () => db.close();
      } catch (e) {
        console.error("[conversationCacheDB] put error", e);
      }
    }, DEBOUNCE_MS);

    pendingWrites.set(convo.id, { convo, timer });
  },

  /**
   * Partially update a cached conversation (read-modify-write).
   * Only the fields present in `patch` are updated; all other fields
   * are preserved from the existing record.  If no record exists, the
   * call is silently skipped.
   */
  async patch(id: string, patch: Partial<CachedConversation>): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);

      const existing: CachedConversation | undefined = await new Promise(
        (res) => {
          const r = store.get(id);
          r.onsuccess = () => res(r.result);
          r.onerror = () => res(undefined);
        },
      );

      if (existing) {
        store.put({ ...existing, ...patch });
      }

      await new Promise<void>((resolve) => {
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); resolve(); };
      });
    } catch (e) {
      console.error("[conversationCacheDB] patch error", e);
    }
  },

  /**
   * Batch-write many conversations from an RTK/server response.
   * Reads existing IDB state first and skips any entry where the
   * cached version is already newer (socket-updated data is preserved).
   */
  async batchPut(convos: CachedConversation[]): Promise<void> {
    if (!convos.length) return;
    try {
      const existing = await this.getAll();
      const existingMap = new Map(existing.map((c) => [c.id, c]));

      const toWrite = convos.filter((c) => {
        const ex = existingMap.get(c.id);
        return !ex || c.lastUpdated >= ex.lastUpdated;
      });

      if (!toWrite.length) return;

      const db = await openDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        const s = tx.objectStore(STORE);
        toWrite.forEach((c) => s.put(c));
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      });
    } catch (e) {
      console.error("[conversationCacheDB] batchPut error", e);
    }
  },

  /** Return a single cached conversation by id, or null if not found. */
  async getById(id: string): Promise<CachedConversation | null> {
    try {
      const db = await openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(id);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => resolve(null);
        tx.oncomplete = () => db.close();
      });
    } catch {
      return null;
    }
  },

  /** Return all cached conversations. */
  async getAll(): Promise<CachedConversation[]> {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).getAll();
        req.onsuccess = () => resolve(req.result ?? []);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      });
    } catch {
      return [];
    }
  },

  /** Destroy the entire database (called on logout). */
  destroyAll(): Promise<void> {
    // Flush all pending debounced writes so they don't fire after logout
    pendingWrites.forEach(({ timer }) => clearTimeout(timer));
    pendingWrites.clear();

    return new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
  },
};

export default conversationCacheDB;
